import type { Column } from '@features/columns/models'
import {
  timeZoneAbbreviation,
  zonedIsoDate,
  zonedParts,
  type MomentZones,
  type ZonedParts,
} from '@features/shared/utils/timeZones'

export interface DisplayFormatOption {
  value: string
  label: string
}

export interface DisplayFormatDefinition {
  id: string
  name: string
}

type AppDataTypeFormatDefaults = Record<
  string,
  {
    defaultDisplayFormatId: string | null
    defaultDisplayFormatName?: string | null
    allowedFormats?: string[]
  }
>

const DISPLAY_FORMATS_BY_APP_DATA_TYPE_NAME: Record<string, DisplayFormatDefinition[]> = {
  boolean: [
    { id: '3be8af3d-cb0b-4e4e-b161-5ba0f09e9ecf', name: 'checkbox' },
    { id: '5fac3eb0-1f63-430b-bc9c-6704815cd5e5', name: 'true_false' },
    { id: '5f5cb6ac-8642-4212-af32-ba74bc3dcd30', name: 'yes_no' },
  ],
  datetime: [
    { id: 'f8465c6e-9c8d-4bcd-9b1e-ad2482c0b1b3', name: 'M/D/YYYY' },
    { id: '708a275b-4e4a-4fe6-aba8-248f036577de', name: 'MM/DD/YYYY' },
    { id: '082a9fb7-8b88-47dc-9f45-c603f0d38b18', name: 'YYYY-MM-DD' },
    { id: '1f5a911f-2ac9-4580-89f7-bc6c839ec339', name: 'Mon D, YYYY' },
    { id: '2f9b02a9-b396-44e5-919a-69725bf2d92a', name: 'Month D, YYYY' },
    { id: 'c85aa67e-6c5e-44be-bd72-0537c6ac8638', name: 'M/D/YYYY h:mm A' },
    { id: '056b2d0e-aeda-4ed8-a4e9-2abbd1fcc349', name: 'MM/DD/YYYY hh:mm A' },
    { id: '431fe5d4-4a0a-4bc3-beb4-2e11ca3432e7', name: 'YYYY-MM-DD HH:mm' },
    { id: 'd5f05d6b-9304-4ed9-ab6e-608d0ee1f759', name: 'Mon D, YYYY h:mm A' },
    { id: '74e1188d-be1a-415b-add2-717536db2b38', name: 'Month D, YYYY h:mm A' },
    { id: '6402ba7a-4c30-4bf2-954d-16ec57389fcb', name: 'Www, Mon D, YYYY h:mm A' },
    { id: '8c4fb8c4-ed5b-4638-be14-df1e593f600b', name: 'Weekday, Month D, YYYY h:mm A' },
    { id: '215da9ad-2299-4076-9290-2296cc81ac07', name: 'M/D/YYYY h:mm:ss A' },
    { id: '080ef00f-043f-4a78-a4e9-14e5aef94fab', name: 'YYYY-MM-DD HH:mm:ss' },
  ],
  duration: [{ id: 'baecd344-dea6-5e2d-9a8b-dfc0be5e429a', name: 'duration' }],
  date: [
    { id: 'c39b62fe-10ec-4942-87af-5a59d20e0089', name: 'M/D/YY' },
    { id: '6a6b74f3-7957-4d0a-8081-94a8711e9301', name: 'MM/DD/YY' },
    { id: 'fd52ed76-09aa-4e7a-aa6a-0bd99e84a49b', name: 'M/D/YYYY' },
    { id: '330c51b4-3a75-497e-b8a7-35ace080fcc2', name: 'MM/DD/YYYY' },
    { id: '03e090e0-bf12-4b59-aedc-42e89a340a1a', name: 'M-D-YY' },
    { id: '83475c39-37d9-4022-90ff-3c35e154532c', name: 'MM-DD-YY' },
    { id: '4ebeac42-c9f3-4f2c-978b-48dc5c74791d', name: 'M-D-YYYY' },
    { id: '10b415f2-bfbe-4c32-94d7-10db3c93d742', name: 'MM-DD-YYYY' },
    { id: '856b8b1e-4613-446f-b288-f4f033b99729', name: 'YYYY-M-D' },
    { id: '753cc1b6-72e7-4e1a-8eaf-622d9a5ee944', name: 'YYYY-MM-DD' },
    { id: '7ca8b2f9-7973-48ba-8209-46b14ea65309', name: 'Mon D, YY' },
    { id: 'ed0e1a46-ea61-46e3-b709-7b2990376332', name: 'Mon DD, YY' },
    { id: '667aa4b9-3ea6-4231-9898-bbfb59fa2dd1', name: 'Mon D, YYYY' },
    { id: '5d7a61b7-4f83-4a1f-98ec-b283fb000863', name: 'Mon DD, YYYY' },
    { id: 'd6f9eb57-5398-48db-8794-7687fe349156', name: 'Month D, YY' },
    { id: 'a8c58b35-f93a-455e-bd72-b1e2fb91fab9', name: 'Month DD, YY' },
    { id: '857c663f-e0e0-4a64-951c-7873737584dc', name: 'Month D, YYYY' },
    { id: '942a210b-be79-461f-ab3b-46d8830ddd4f', name: 'Month DD, YYYY' },
    { id: '09e01aa6-552c-4653-bcdb-a7eecab25f63', name: 'Www, Mon D, YYYY' },
    { id: 'edfae078-ca44-45ac-b2ac-a567a3e04ff7', name: 'Www, Month D, YYYY' },
    { id: '9a37bc53-898b-4169-b772-62a4d6ae90d8', name: 'Weekday, Mon D, YYYY' },
    { id: 'd0898385-47cc-4924-9960-008a1c925e0a', name: 'Weekday, Month D, YYYY' },
    { id: '6872279d-cdfa-402a-aac9-e9f39ef8ae4e', name: 'W, Mon D, YYYY' },
    { id: '7e6aee7b-c025-4005-b9f8-a93935bc213c', name: 'W, Month D, YYYY' },
  ],
  email: [{ id: 'd1fae57e-d286-47d3-8583-7285e74045c9', name: 'email' }],
  id: [{ id: '9be941b2-1d55-49e5-94e2-b6d31c7a9c4a', name: 'id' }],
  number: [
    { id: '77f634f6-25c5-441d-a32f-cf311ccf19b2', name: 'decimal' },
    { id: 'a9673d80-564a-47ba-a09e-8bff6f948fc7', name: 'currency' },
    { id: '2c99a0f9-e9fd-490d-91b7-5718a127a245', name: 'fraction' },
    { id: 'e9b49c37-479e-4619-9b55-e06f408fed81', name: 'percent' },
  ],
  phone_number: [
    { id: '41c6e507-b473-4970-a765-1567123a4ef7', name: '(###) ###-####' },
    { id: '63a0fb2e-50f7-4000-bd75-a17fc73a5ca4', name: '###-###-####' },
  ],
  text: [{ id: '7f03cb3a-0c24-4be2-8eae-fb091822d300', name: 'text' }],
  url: [{ id: '9262ad41-7833-4dd5-8d15-400628353b05', name: 'url' }],
}

const DISPLAY_FORMAT_EXAMPLE_LABELS: Record<string, string> = {
  'M/D/YY': '1/1/00',
  'MM/DD/YY': '01/01/00',
  'M/D/YYYY': '1/1/2000',
  'MM/DD/YYYY': '01/01/2000',
  'M-D-YY': '1-1-00',
  'MM-DD-YY': '01-01-00',
  'M-D-YYYY': '1-1-2000',
  'MM-DD-YYYY': '01-01-2000',
  'YYYY-M-D': '2000-1-1',
  'YYYY-MM-DD': '2000-01-01',
  'Mon D, YY': 'Jan 1, 00',
  'Mon DD, YY': 'Jan 01, 00',
  'Mon D, YYYY': 'Jan 1, 2000',
  'Mon DD, YYYY': 'Jan 01, 2000',
  'Month D, YY': 'January 1, 00',
  'Month DD, YY': 'January 01, 00',
  'Month D, YYYY': 'January 1, 2000',
  'Month DD, YYYY': 'January 01, 2000',
  'Www, Mon D, YYYY': 'Mon, Jan 1, 2000',
  'Www, Month D, YYYY': 'Mon, January 1, 2000',
  'Weekday, Mon D, YYYY': 'Monday, Jan 1, 2000',
  'Weekday, Month D, YYYY': 'Monday, January 1, 2000',
  'W, Mon D, YYYY': 'M, Jan 1, 2000',
  'W, Month D, YYYY': 'M, January 1, 2000',
  'M/D/YYYY h:mm A': '1/1/2000 1:30 PM',
  'MM/DD/YYYY hh:mm A': '01/01/2000 01:30 PM',
  'YYYY-MM-DD HH:mm': '2000-01-01 13:30',
  'Mon D, YYYY h:mm A': 'Jan 1, 2000 1:30 PM',
  'Month D, YYYY h:mm A': 'January 1, 2000 1:30 PM',
  'Www, Mon D, YYYY h:mm A': 'Mon, Jan 1, 2000 1:30 PM',
  'Weekday, Month D, YYYY h:mm A': 'Monday, January 1, 2000 1:30 PM',
  'M/D/YYYY h:mm:ss A': '1/1/2000 1:30:45 PM',
  'YYYY-MM-DD HH:mm:ss': '2000-01-01 13:30:45',
  duration: '2d 3h 4m 5s',
}

export function normalizeMetadataName(value: string | null | undefined): string {
  return (
    value
      ?.trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_') ?? ''
  )
}

export function formatMetadataName(value: string | null | undefined): string {
  const normalized = normalizeMetadataName(value)
  if (!normalized) return ''

  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function formatDisplayFormatLabel(value: string): string {
  if (DISPLAY_FORMAT_EXAMPLE_LABELS[value]) return DISPLAY_FORMAT_EXAMPLE_LABELS[value]
  if (value.includes('#')) return value
  return formatMetadataName(value)
}

export function getKnownDisplayFormatsForAppDataTypeName(
  appDataTypeName: string | null | undefined
): DisplayFormatDefinition[] {
  const normalizedName = normalizeMetadataName(appDataTypeName)
  if (!normalizedName) return []

  return DISPLAY_FORMATS_BY_APP_DATA_TYPE_NAME[normalizedName] ?? []
}

export function getKnownDisplayFormatForAppDataTypeName(
  appDataTypeName: string | null | undefined,
  displayFormatName: string | null | undefined
): DisplayFormatDefinition | null {
  const normalizedDisplayFormatName = normalizeMetadataName(displayFormatName)
  if (!normalizedDisplayFormatName) return null

  return (
    getKnownDisplayFormatsForAppDataTypeName(appDataTypeName).find(
      (format) => normalizeMetadataName(format.name) === normalizedDisplayFormatName
    ) ?? null
  )
}

// Looked up for every formatted cell, so index the catalog once.
const KNOWN_DISPLAY_FORMAT_NAME_BY_ID = new Map<string, string>()
for (const formats of Object.values(DISPLAY_FORMATS_BY_APP_DATA_TYPE_NAME)) {
  for (const format of formats) {
    if (!KNOWN_DISPLAY_FORMAT_NAME_BY_ID.has(format.id)) {
      KNOWN_DISPLAY_FORMAT_NAME_BY_ID.set(format.id, format.name)
    }
  }
}

export function getKnownDisplayFormatNameById(displayFormatId: string | null | undefined): string {
  if (!displayFormatId) return ''
  return KNOWN_DISPLAY_FORMAT_NAME_BY_ID.get(displayFormatId) ?? ''
}

export function getDefaultDisplayFormatForAppDataTypeName(
  appDataTypeName: string | null | undefined
): DisplayFormatDefinition | null {
  return getKnownDisplayFormatsForAppDataTypeName(appDataTypeName)[0] ?? null
}

function getDataTypeFormats(
  column: Column | null,
  appDataTypeFormats: AppDataTypeFormatDefaults
): DisplayFormatDefinition[] {
  const appDataTypeName = column?.app_data_type_name
  if (!appDataTypeName) return []

  const knownFormats = getKnownDisplayFormatsForAppDataTypeName(appDataTypeName)
  const allowedFormats = column?.app_data_type_id
    ? appDataTypeFormats[column.app_data_type_id]?.allowedFormats
    : null
  if (!allowedFormats || allowedFormats.length === 0) return knownFormats

  const knownFormatsByName = new Map(
    knownFormats.map((format) => [normalizeMetadataName(format.name), format])
  )
  const matchedAllowedFormats = allowedFormats
    .map((formatName) => knownFormatsByName.get(normalizeMetadataName(formatName)))
    .filter((format): format is DisplayFormatDefinition => Boolean(format))

  return matchedAllowedFormats.length > 0 ? matchedAllowedFormats : knownFormats
}

export function getEffectiveDisplayFormatId(
  column: Column | null,
  appDataTypeFormats: AppDataTypeFormatDefaults
): string {
  const effectiveDisplayFormatId =
    column?.display_format_id ??
    (column?.app_data_type_id
      ? appDataTypeFormats[column.app_data_type_id]?.defaultDisplayFormatId
      : null) ??
    getDefaultDisplayFormatForAppDataTypeName(column?.app_data_type_name)?.id ??
    ''

  if (!isFormulaBooleanColumn(column)) return effectiveDisplayFormatId

  const effectiveDisplayFormatName =
    getKnownDisplayFormatNameById(effectiveDisplayFormatId) ||
    column?.display_format_name ||
    (column?.app_data_type_id
      ? appDataTypeFormats[column.app_data_type_id]?.defaultDisplayFormatName
      : null) ||
    ''

  if (
    effectiveDisplayFormatId &&
    normalizeMetadataName(effectiveDisplayFormatName) !== 'checkbox'
  ) {
    return effectiveDisplayFormatId
  }

  return (
    getKnownDisplayFormatForAppDataTypeName('boolean', 'yes_no')?.id ?? effectiveDisplayFormatId
  )
}

export function getEffectiveDisplayFormatName(
  column: Column | null,
  appDataTypeFormats: AppDataTypeFormatDefaults
): string {
  const effectiveId = getEffectiveDisplayFormatId(column, appDataTypeFormats)
  const configuredDefault = column?.app_data_type_id
    ? appDataTypeFormats[column.app_data_type_id]
    : null
  const matchedFormat = getDataTypeFormats(column, appDataTypeFormats).find(
    (format) => format.id === effectiveId
  )
  const configuredDefaultName =
    configuredDefault?.defaultDisplayFormatId === effectiveId
      ? configuredDefault.defaultDisplayFormatName
      : null

  return matchedFormat?.name ?? column?.display_format_name ?? configuredDefaultName ?? ''
}

/**
 * Materialize the effective display format on a column before passing it to
 * presentation-only table components. Columns normally carry an explicit
 * format, but legacy/derived metadata can omit it and rely on the configured
 * app-datatype default instead.
 */
export function resolveColumnDisplayFormat(
  column: Column,
  appDataTypeFormats: AppDataTypeFormatDefaults
): Column {
  const displayFormatId = getEffectiveDisplayFormatId(column, appDataTypeFormats)
  const displayFormatName = getEffectiveDisplayFormatName(column, appDataTypeFormats)

  if (
    (!displayFormatId || displayFormatId === column.display_format_id) &&
    (!displayFormatName || displayFormatName === column.display_format_name)
  ) {
    return column
  }

  return {
    ...column,
    display_format_id: displayFormatId || column.display_format_id,
    display_format_name: displayFormatName || column.display_format_name,
  }
}

export function getColumnDisplayFormatName(column: Column | null): string {
  if (!column) return ''
  return (
    getKnownDisplayFormatNameById(column.display_format_id) ||
    column.display_format_name ||
    getDefaultDisplayFormatForAppDataTypeName(column.app_data_type_name)?.name ||
    ''
  )
}

export function shouldRenderBooleanAsCheckbox(column: Column): boolean {
  if (column.type !== 'boolean' || isFormulaBooleanColumn(column)) return false
  const displayFormatName = normalizeMetadataName(getColumnDisplayFormatName(column))
  return !displayFormatName || displayFormatName === 'checkbox'
}

export function getDisplayFormatOptions(
  column: Column | null,
  appDataTypeFormats: AppDataTypeFormatDefaults
): DisplayFormatOption[] {
  const formatsById = new Map<string, DisplayFormatDefinition>()

  for (const format of getDataTypeFormats(column, appDataTypeFormats)) {
    if (isFormulaBooleanColumn(column) && normalizeMetadataName(format.name) === 'checkbox') {
      continue
    }
    formatsById.set(format.id, format)
  }

  if (isFormulaBooleanColumn(column)) {
    const yesNoFormat = getKnownDisplayFormatForAppDataTypeName('boolean', 'yes_no')
    if (yesNoFormat) formatsById.set(yesNoFormat.id, yesNoFormat)
  }

  if (
    column?.display_format_id &&
    column.display_format_name &&
    !(
      isFormulaBooleanColumn(column) &&
      normalizeMetadataName(column.display_format_name) === 'checkbox'
    ) &&
    !formatsById.has(column.display_format_id)
  ) {
    formatsById.set(column.display_format_id, {
      id: column.display_format_id,
      name: column.display_format_name,
    })
  }

  if (column?.app_data_type_id) {
    const configuredDefault = appDataTypeFormats[column.app_data_type_id]
    if (
      configuredDefault?.defaultDisplayFormatId &&
      configuredDefault.defaultDisplayFormatName &&
      !(
        isFormulaBooleanColumn(column) &&
        normalizeMetadataName(configuredDefault.defaultDisplayFormatName) === 'checkbox'
      ) &&
      !formatsById.has(configuredDefault.defaultDisplayFormatId)
    ) {
      formatsById.set(configuredDefault.defaultDisplayFormatId, {
        id: configuredDefault.defaultDisplayFormatId,
        name: configuredDefault.defaultDisplayFormatName,
      })
    }
  }

  return [...formatsById.values()].map((format) => ({
    value: format.id,
    label: formatDisplayFormatLabel(format.name),
  }))
}

/** Fallback when column metadata omits display_decimal_places; mirrors the
 * seeded app-datatype default for `number`. */
export const DEFAULT_DISPLAY_DECIMAL_PLACES = 2

/** Widest offered scale when the column has no storage_decimal_places cap. */
const MAX_DISPLAY_DECIMAL_PLACES_FALLBACK = 6

export function isNumberAppDataTypeColumn(column: Column | null | undefined): boolean {
  return normalizeMetadataName(column?.app_data_type_name) === 'number'
}

/**
 * Options for the Field Settings "Decimal Places" select. Display precision
 * can't exceed the column's storage scale (mirrors the DB check constraint on
 * app-datatype defaults), so the range is capped by storage_decimal_places
 * when it's known.
 */
export function getDisplayDecimalPlacesOptions(
  column: Column | null | undefined
): DisplayFormatOption[] {
  const max = column?.storage_decimal_places ?? MAX_DISPLAY_DECIMAL_PLACES_FALLBACK
  return Array.from({ length: Math.max(0, max) + 1 }, (_, places) => ({
    value: String(places),
    label: String(places),
  }))
}

/** The decimal-places value the Field Settings select should show for a column. */
export function getEffectiveDisplayDecimalPlaces(column: Column | null | undefined): number {
  return column?.display_decimal_places ?? DEFAULT_DISPLAY_DECIMAL_PLACES
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0')
}

function parseDateValue(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  if (typeof value === 'string') {
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (dateOnly) {
      const [, year, month, day] = dateOnly
      const parsed = new Date(Number(year), Number(month) - 1, Number(day))
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
  }

  const parsed = new Date(value as string)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

// English names, matching the en-US patterns above. Building them with
// toLocaleDateString costs a new Intl formatter per call, and this runs for
// every date cell a table renders, sizes, or searches.
const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const DATE_PATTERN_TOKEN = /Weekday|Www|Month|Mon|YYYY|YY|MM|M|DD|D|HH|hh|h|mm|ss|A|W/g

function formatDateToken(date: ZonedParts, token: string): string {
  switch (token) {
    case 'Weekday':
      return WEEKDAY_NAMES[date.weekday]
    case 'Www':
      return WEEKDAY_NAMES[date.weekday].slice(0, 3)
    case 'W':
      return WEEKDAY_NAMES[date.weekday][0]
    case 'Month':
      return MONTH_NAMES[date.month - 1]
    case 'Mon':
      return MONTH_NAMES[date.month - 1].slice(0, 3)
    case 'YYYY':
      return String(date.year)
    case 'YY':
      return pad(date.year % 100)
    case 'MM':
      return pad(date.month)
    case 'M':
      return String(date.month)
    case 'DD':
      return pad(date.day)
    case 'D':
      return String(date.day)
    case 'HH':
      return pad(date.hours)
    case 'hh':
      return pad(date.hours % 12 || 12)
    case 'h':
      return String(date.hours % 12 || 12)
    case 'mm':
      return pad(date.minutes)
    case 'ss':
      return pad(date.seconds)
    case 'A':
      return date.hours >= 12 ? 'PM' : 'AM'
    default:
      return token
  }
}

/** What the browser's own clock reads at `date`. */
function localParts(date: Date): ZonedParts {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
    seconds: date.getSeconds(),
    weekday: date.getDay(),
  }
}

function formatPartsWithPattern(parts: ZonedParts, pattern: string): string {
  return pattern.replace(DATE_PATTERN_TOKEN, (token) => formatDateToken(parts, token))
}

function formatDateWithPattern(date: Date, pattern: string): string {
  return formatPartsWithPattern(localParts(date), pattern)
}

// ======================================================
// Moments in the viewer's time zone
// ======================================================

/**
 * The zones datetime moments show in, while the viewer's Primary Time Zone isn't
 * where their browser is (the settings store keeps it current); null shows
 * them on the browser's clock, as everywhere else.
 */
let momentDisplayZones: MomentZones | null = null

export function setMomentDisplayZones(zones: MomentZones | null): void {
  momentDisplayZones = zones?.secondaryTimeZone ? zones : null
}

/** A value that names a moment: a `Date`, or a date and time with `Z` or an offset. */
function isMomentValue(value: unknown): boolean {
  return (
    value instanceof Date ||
    (typeof value === 'string' &&
      /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}(:?\d{2})?)$/i.test(value))
  )
}

const TIME_TOKEN = /HH|hh|h/

/**
 * A moment as the pattern reads in the viewer's zone, then where they are now
 * in parentheses (`formatMoment`'s way): "Oct 16, 2026 9:00 AM EDT (6:00 AM PDT)".
 */
function formatMomentWithPattern(date: Date, pattern: string, zones: MomentZones): string {
  const parts = zonedParts(date, zones.timeZone)
  const hasTime = TIME_TOKEN.test(pattern)
  const primary = formatPartsWithPattern(parts, pattern)
  const secondaryZone = zones.secondaryTimeZone
  if (!secondaryZone) return primary
  const secondaryParts = zonedParts(date, secondaryZone)
  if (!hasTime) {
    const secondary = formatPartsWithPattern(secondaryParts, pattern)
    return secondary === primary ? primary : `${primary} (${secondary})`
  }
  // Its time alone on the same day, else the column's whole pattern.
  const sameDay = zonedIsoDate(parts) === zonedIsoDate(secondaryParts)
  const timePattern = pattern.includes('HH') ? 'HH:mm' : 'h:mm A'
  const secondary = formatPartsWithPattern(secondaryParts, sameDay ? timePattern : pattern)
  return (
    `${primary} ${timeZoneAbbreviation(zones.timeZone, date)} ` +
    `(${secondary} ${timeZoneAbbreviation(secondaryZone, date)})`
  )
}

interface ParsedTimeValue {
  hours: number
  minutes: number
  seconds: number
}

function parseTimeValue(value: unknown): ParsedTimeValue | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return {
      hours: value.getHours(),
      minutes: value.getMinutes(),
      seconds: value.getSeconds(),
    }
  }

  if (typeof value !== 'string') return null
  const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/)
  if (!match) return null

  const hours = Number(match[1])
  const minutes = Number(match[2])
  const seconds = Number(match[3] ?? 0)
  if (hours > 23 || minutes > 59 || seconds > 59) return null
  return { hours, minutes, seconds }
}

function formatTimeWithPattern(time: ParsedTimeValue, pattern: string): string {
  const hours12 = time.hours % 12 || 12
  const replacements: Record<string, string> = {
    HH: pad(time.hours),
    hh: pad(hours12),
    h: String(hours12),
    mm: pad(time.minutes),
    ss: pad(time.seconds),
    A: time.hours >= 12 ? 'PM' : 'AM',
  }

  return pattern.replace(/HH|hh|h|mm|ss|A/g, (token) => replacements[token] ?? token)
}

const ISO_DURATION_PATTERN =
  /^([+-])?P(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i

/** A unit a duration can be shown in. Years and months vary in length; the rest don't. */
export type DurationUnit = 'y' | 'mo' | 'w' | 'd' | 'h' | 'm' | 's' | 'ms'

/** The units whose length never varies, so a duration alone fixes how many there are. */
export type FixedDurationUnit = Exclude<DurationUnit, 'y' | 'mo'>

const FIXED_UNIT_MS: Record<FixedDurationUnit, number> = {
  w: 7 * 24 * 60 * 60 * 1000,
  d: 24 * 60 * 60 * 1000,
  h: 60 * 60 * 1000,
  m: 60 * 1000,
  s: 1000,
  ms: 1,
}

/** Days down to milliseconds: every length shown as a fixed, comparable amount. */
export const DEFAULT_DURATION_UNITS: readonly FixedDurationUnit[] = ['d', 'h', 'm', 's', 'ms']

export interface DurationFormatOptions {
  /**
   * The fixed-length units to show, largest first. Anything below the last is
   * dropped, so leaving out `ms` shows whole seconds. Defaults to days down to
   * milliseconds.
   */
  units?: readonly FixedDurationUnit[]
  /**
   * Years and months, shown before the fixed units. Their length depends on
   * the calendar, so they need the moment the duration starts from, and are
   * off unless asked for: a duration stays a deterministic length by default.
   */
  calendarUnits?: { from: Date }
}

/** `date` plus whole months, clamped to the last day of a shorter month. */
function addCalendarMonths(date: Date, months: number): Date {
  const result = new Date(date.getTime())
  result.setDate(1)
  result.setMonth(result.getMonth() + months)
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate()
  result.setDate(Math.min(date.getDate(), lastDay))
  return result
}

/**
 * `3m 24s 312ms`: one integer per unit, largest first, leaving out units that
 * are zero. Nothing smaller than the last unit shows, so a duration with no
 * milliseconds simply ends at seconds. A duration of zero shows `0` in the
 * smallest unit.
 */
export function formatDuration(totalMs: number, options: DurationFormatOptions = {}): string {
  const units = options.units ?? DEFAULT_DURATION_UNITS
  const sign = totalMs < 0 ? '-' : ''
  let remaining = Math.floor(Math.abs(totalMs) + 1e-6)
  const parts: string[] = []

  if (options.calendarUnits) {
    const from = options.calendarUnits.from
    const end = from.getTime() + remaining
    // Whole months counted from the start, each landing on the same day of
    // the month or that month's last day (Jan 31 plus a month is Feb 28).
    let months = 0
    while (addCalendarMonths(from, months + 1).getTime() <= end) months += 1
    const years = Math.floor(months / 12)
    if (years > 0) parts.push(`${years}y`)
    if (months % 12 > 0) parts.push(`${months % 12}mo`)
    remaining = end - addCalendarMonths(from, months).getTime()
  }

  for (const unit of units) {
    const size = FIXED_UNIT_MS[unit]
    const amount = Math.floor(remaining / size)
    remaining -= amount * size
    if (amount > 0) parts.push(`${amount}${unit}`)
  }
  if (parts.length === 0) return units.includes('s') ? '0s' : `0${units[units.length - 1] ?? 's'}`
  return `${sign}${parts.join(' ')}`
}

/** An ISO-8601 duration as milliseconds, or null when it isn't one. */
function parseIsoDurationMs(value: string): number | null {
  const match = value.match(ISO_DURATION_PATTERN)
  if (!match) return null
  const amounts = [match[2], match[3], match[4], match[5], match[6]]
  if (!amounts.some((amount) => amount !== undefined)) return null
  const [weeks, days, hours, minutes, seconds] = amounts.map((amount) => Number(amount ?? 0))
  const ms =
    weeks * FIXED_UNIT_MS.w +
    days * FIXED_UNIT_MS.d +
    hours * FIXED_UNIT_MS.h +
    minutes * FIXED_UNIT_MS.m +
    seconds * FIXED_UNIT_MS.s
  return match[1] === '-' ? -ms : ms
}

/**
 * A duration value for table display: a backend ISO-8601 timedelta (Pydantic's
 * JSON form) or a number of milliseconds, shown by formatDuration.
 */
function formatDurationValue(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return formatDuration(value)
  if (typeof value !== 'string') return null
  const ms = parseIsoDurationMs(value)
  return ms === null ? null : formatDuration(ms)
}

const decimalFormatters = new Map<number, Intl.NumberFormat>()
const currencyFormatters = new Map<number, Intl.NumberFormat>()

function getDecimalFormatter(fractionDigits: number): Intl.NumberFormat {
  let formatter = decimalFormatters.get(fractionDigits)
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    decimalFormatters.set(fractionDigits, formatter)
  }
  return formatter
}

function getCurrencyFormatter(fractionDigits: number): Intl.NumberFormat {
  let formatter = currencyFormatters.get(fractionDigits)
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    currencyFormatters.set(fractionDigits, formatter)
  }
  return formatter
}

interface ParsedDecimalString {
  negative: boolean
  whole: string
  fraction: string
}

/** `shift` moves the decimal point right (2 turns a fraction into percent). */
function parseDecimalString(value: string, shift = 0): ParsedDecimalString | null {
  const match = value.trim().match(/^([+-]?)(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/)
  if (!match) return null

  const coefficientWhole = match[2] ?? '0'
  const coefficientFraction = match[3] ?? match[4] ?? ''
  const exponent = Number(match[5] ?? 0)
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10_000) return null

  const coefficientDigits = `${coefficientWhole}${coefficientFraction}`
  const decimalIndex = coefficientWhole.length + exponent + shift
  let whole: string
  let fraction: string

  if (decimalIndex <= 0) {
    whole = '0'
    fraction = `${'0'.repeat(-decimalIndex)}${coefficientDigits}`
  } else if (decimalIndex >= coefficientDigits.length) {
    whole = `${coefficientDigits}${'0'.repeat(decimalIndex - coefficientDigits.length)}`
    fraction = ''
  } else {
    whole = coefficientDigits.slice(0, decimalIndex)
    fraction = coefficientDigits.slice(decimalIndex)
  }

  return {
    negative: match[1] === '-',
    whole: whole.replace(/^0+(?=\d)/, '') || '0',
    fraction,
  }
}

function incrementDecimalDigits(value: string): string {
  const digits = value.split('')
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    if (digits[index] !== '9') {
      digits[index] = String(Number(digits[index]) + 1)
      return digits.join('')
    }
    digits[index] = '0'
  }
  return `1${digits.join('')}`
}

function formatExactDecimalString(
  value: unknown,
  {
    maximumFractionDigits,
    minimumFractionDigits = 0,
    prefix = '',
    suffix = '',
    shift = 0,
  }: {
    maximumFractionDigits: number
    minimumFractionDigits?: number
    prefix?: string
    suffix?: string
    shift?: number
  }
): string | null {
  if (typeof value !== 'string' && typeof value !== 'bigint') return null
  const parsed = parseDecimalString(String(value), shift)
  if (!parsed) return null

  const paddedFraction = parsed.fraction.padEnd(maximumFractionDigits + 1, '0')
  let roundedDigits = `${parsed.whole}${paddedFraction.slice(0, maximumFractionDigits)}`
  if (paddedFraction[maximumFractionDigits] >= '5') {
    roundedDigits = incrementDecimalDigits(roundedDigits)
  }

  const wholeLength = Math.max(1, roundedDigits.length - maximumFractionDigits)
  const whole = roundedDigits.slice(0, wholeLength).replace(/^0+(?=\d)/, '') || '0'
  const fraction = roundedDigits
    .slice(wholeLength)
    .padStart(maximumFractionDigits, '0')
    .replace(/0+$/, '')
    .padEnd(minimumFractionDigits, '0')
  const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return `${parsed.negative ? '-' : ''}${prefix}${groupedWhole}${fraction ? `.${fraction}` : ''}${suffix}`
}

function coerceBooleanValue(value: unknown): boolean {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (
      normalized === 'false' ||
      normalized === '0' ||
      normalized === 'no' ||
      normalized === 'off'
    ) {
      return false
    }
    if (
      normalized === 'true' ||
      normalized === '1' ||
      normalized === 'yes' ||
      normalized === 'on'
    ) {
      return true
    }
  }

  return Boolean(value)
}

export function formatColumnValue(value: unknown, column: Column): string {
  if (value === null || value === undefined) return ''

  if (Array.isArray(value)) {
    const arrayFormatting = column.formatting?.array
    const mode = arrayFormatting?.mode ?? 'join'
    if (value.length === 0 && arrayFormatting?.empty_label !== undefined) {
      return arrayFormatting.empty_label
    }
    if (mode === 'count') return String(value.length)
    if (value.length === 0) return ''

    const formattedValues = value.map((item) => formatColumnValue(item, column))
    if (mode === 'collapse' && formattedValues.length > 1) {
      return arrayFormatting?.multiple_label ?? 'Multiple'
    }
    return formattedValues.join(arrayFormatting?.separator ?? ', ')
  }

  const displayFormatName = getColumnDisplayFormatName(column)

  switch (column.type) {
    case 'number': {
      // Percent values are stored as fractions: 0.125 shows as 12.5%.
      const isPercent = normalizeMetadataName(displayFormatName) === 'percent'
      // Pad every cell to the column's decimal places so fraction digits stay
      // consistent down the column; ragged decimals break magnitude scanning.
      const fractionDigits = Math.max(
        0,
        column.display_decimal_places ?? DEFAULT_DISPLAY_DECIMAL_PLACES
      )
      const exact = formatExactDecimalString(value, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
        suffix: isPercent ? '%' : '',
        shift: isPercent ? 2 : 0,
      })
      if (exact !== null) return exact

      const num = Number(value)
      if (Number.isNaN(num)) return String(value)
      if (isPercent) return `${getDecimalFormatter(fractionDigits).format(num * 100)}%`
      return getDecimalFormatter(fractionDigits).format(num)
    }
    case 'currency': {
      const fractionDigits = Math.max(
        0,
        column.display_decimal_places ?? DEFAULT_DISPLAY_DECIMAL_PLACES
      )
      const exact = formatExactDecimalString(value, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
        prefix: '$',
      })
      if (exact !== null) return exact

      const amount = Number(value)
      return Number.isNaN(amount)
        ? String(value)
        : getCurrencyFormatter(fractionDigits).format(amount)
    }
    case 'date':
    case 'datetime': {
      const date = parseDateValue(value)
      if (!date) return String(value)
      const pattern =
        displayFormatName || (column.type === 'datetime' ? 'Mon D, YYYY h:mm A' : 'Mon D, YYYY')
      // A moment shows in the viewer's zone; a wall-clock value (no offset) as it reads.
      if (column.type === 'datetime' && momentDisplayZones && isMomentValue(value)) {
        return formatMomentWithPattern(date, pattern, momentDisplayZones)
      }
      return formatDateWithPattern(date, pattern)
    }
    case 'time': {
      const time = parseTimeValue(value)
      if (!time) return String(value)
      return formatTimeWithPattern(time, displayFormatName || 'HH:mm:ss')
    }
    case 'duration':
      return formatDurationValue(value) ?? String(value)
    case 'boolean': {
      const normalizedDisplayFormat = normalizeMetadataName(displayFormatName)
      const booleanValue = coerceBooleanValue(value)
      if (
        normalizedDisplayFormat === 'yes_no' ||
        (isFormulaBooleanColumn(column) &&
          (!normalizedDisplayFormat || normalizedDisplayFormat === 'checkbox'))
      ) {
        return booleanValue ? 'Yes' : 'No'
      }
      if (normalizedDisplayFormat === 'true_false') return booleanValue ? 'True' : 'False'
      return ''
    }
    case 'text':
    default:
      return String(value)
  }
}

export function isFormulaBooleanColumn(column: Column | null | undefined): boolean {
  if (!column || column.type !== 'boolean') return false
  return column.source === 'derived' || normalizeMetadataName(column.input_mode_name) === 'formula'
}

/** True when the rendered text summarizes an array instead of showing its literal values. */
export function isColumnArraySummary(value: unknown, column: Column): boolean {
  if (!Array.isArray(value)) return false
  const mode = column.formatting?.array?.mode ?? 'join'
  return mode === 'count' || (mode === 'collapse' && value.length > 1)
}
